import { createColumnHelper } from '@tanstack/react-table';
import React from 'react';
import { Table, Button, GenericModal, SearchActions, unwrapTrpcData } from '@org/ui';
import { trpc } from '@org/clients/school';
import { GroupForm } from '../forms/group';
import { JourneysDialog } from '../journey/journeys-dialog';
import { useTranslation } from 'react-i18next';

type GroupsListProps = {
  onCreateGroup?: () => void;
  onEditGroup?: (group: any) => void;
};

export const GroupsList: React.FC<GroupsListProps> = ({ onCreateGroup, onEditGroup }) => {
  const { data } = trpc.groups.list.useQuery();
  const { data: levels } = trpc.levels.list.useQuery();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [filterValue, setFilterValue] = React.useState("");
  const [groupData, setGroupData] = React.useState<any[]>([]);
  const [levelsData, setLevelsData] = React.useState<any[]>([]);
  const [selectedGroup, setSelectedGroup] = React.useState(null);
  const [isCreating, setIsCreating] = React.useState(false);
  const [showActiveOnly, setShowActiveOnly] = React.useState(true);
  const [journeyDialogFor, setJourneyDialogFor] = React.useState<{
    groupId: string;
    label: string;
  } | null>(null);
  const { t } = useTranslation('admin');

// Update state when `data` changes
  React.useEffect(() => {
    setGroupData(unwrapTrpcData(data) ?? []);
  }, [data]);


  React.useEffect(() => {
    setLevelsData(unwrapTrpcData(levels) ?? []);
  }, [levels]);

  const filteredGroups = React.useMemo(() => {
    if (!showActiveOnly) {
      return groupData;
    }

    return groupData.filter((group) => group?.isActive);
  }, [groupData, showActiveOnly]);

  const selectGroup = (row: any) => {
    if (onEditGroup) {
      onEditGroup(row.original);
      return;
    }

    setIsCreating(false);
    setModalOpen(true)
    setSelectedGroup(row.original);
  }

  // Function to open form for a new user
  const openNewGroupForm = () => {
    if (onCreateGroup) {
      onCreateGroup();
      return;
    }

    setSelectedGroup(null);
    setIsCreating(true);
    setModalOpen(true);
  };

  // Handle filter changes
  const handleFilterChange = (value: string) => {
    setFilterValue(value);
  };

  const onGroupFormSubmit = () => {
    setModalOpen(false);
  }

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor((row: any) => row.description ? `${row.description}` : t('groups.list.table.fallback.noName'), {
      id: 'description',
      header: t('groups.list.table.columns.description'),
    }),
    columnHelper.accessor(
      (row: any) => {
        return  levelsData?.find((level) => level.levelId === row.levelId)?.name;
      },
      {
        id: 'Level',
        header: t('groups.list.table.columns.level'),
      }
    ),
    columnHelper.display({
      id: 'edit',
      header: t('groups.list.table.columns.actions'),
      cell: (info) => {
        const row = info.row.original as any;
        const label = row?.description ?? t('groups.list.table.fallback.noName');
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => selectGroup(info.row)}
            >
              {t('groups.list.actions.edit')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                setJourneyDialogFor({ groupId: row.groupId, label });
              }}
            >
              {t('groups.list.actions.journeys')}
            </Button>
          </div>
        );
      },
    }),
  ];

  return (
    <div>
      <div><SearchActions
          onClickAdd={openNewGroupForm}
          onFilterChange={handleFilterChange}
          showEnabledFilter
          enabledOnly={showActiveOnly}
          onEnabledOnlyChange={setShowActiveOnly}
          enabledOnlyLabel={t('groups.filters.activeOnly')}
        />
        </div>
      <div><Table
          tableData={filteredGroups as any}
          columns={columns}
          getRowId={(row: any) => row.groupId}
          globalFilter={filterValue}  
        /></div>
      {!onCreateGroup && !onEditGroup && modalOpen ? (
        <div><GenericModal
          show={modalOpen}
          onClose={() => setModalOpen(false)}
          title={isCreating ? t('groups.list.modal.createTitle') : t('groups.list.modal.editTitle')}
          body={
            <div>
              <GroupForm defaultValues={selectedGroup || {}}
              onSubmit={onGroupFormSubmit}
              onClose={() => setModalOpen(false)} />
            </div>
          }
          footer={
            <>
            </>
          }
          size="xl"
          centered
        />
        </div>
      ) : null}
      {journeyDialogFor && (
        <JourneysDialog
          entityType="group"
          entityId={journeyDialogFor.groupId}
          entityLabel={journeyDialogFor.label}
          open={!!journeyDialogFor}
          onOpenChange={(o) => {
            if (!o) setJourneyDialogFor(null);
          }}
        />
      )}
    </div>
  )

}
